import React from "react";
import { useSelector } from "react-redux";
import { motion } from "framer-motion";
import { Star } from "lucide-react";
import Hero from "../../components/home/Hero";
import SmartTopicsSection from "../../components/home/SmartTopicsSection";
import HowItWorks from "../../components/home/HowItWorks";
import WhatIsInfoBoxx from "../../components/home/WhatIsInfoBoxx";
import Categories from "../../components/home/Categories";
import Footer from "../../components/footer/Footer";
import CategoryPartnerHub from "../../components/home/CategoryPartnerHub";
import DemoSection from "../../components/home/DemoSection";
import Partners from "../../components/home/Partners";
import BookAppointment from "../../components/home/BookAppointment";
import BecomeAPartner from "../../components/home/BecomeAPartner";
import FinalCTA from "../../components/home/FinalCTA";
import WhyChooseInfoBoxx from "../../components/home/WhyChooseInfoBoxx";
import ArticleAuthFlowModal from "../../components/auth/ArticleAuthFlowModal";
import { useEngagementSubscribePrompt } from "../../hooks/useEngagementSubscribePrompt";

const reveal = {
  hidden: { opacity: 0, y: 40 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.55, ease: "easeOut" },
  },
};

const Section = ({ children }) => (
  <motion.div
    initial="hidden"
    whileInView="visible"
    viewport={{ once: true, margin: "-80px" }}
    variants={reveal}
  >
    {children}
  </motion.div>
);

const Home = () => {
  const { token } = useSelector((s) => s.infoboxx_consumer);
  const isAuthenticated = Boolean(token);

  const { shouldOpen: subscribePromptOpen, dismiss: dismissSubscribePrompt } =
    useEngagementSubscribePrompt({
      enabled: !isAuthenticated,
      requireScroll: false,
      persistDismiss: false,
    });

  return (
    <div className="overflow-x-hidden min-h-screen bg-[#f7f4ec] scroll-smooth [scroll-padding-top:5.5rem]">
      <Hero />
      {/* Target for `/#categories` (Contact, About, Hero CTAs). */}
      <Section>
        <Categories />
      </Section>
      <SmartTopicsSection />

      <Section>
        <HowItWorks />
      </Section>

      <Section>
        <WhatIsInfoBoxx />
      </Section>

      <Section>
        <DemoSection />
      </Section>
      
      <Section>
        <BookAppointment />
      </Section>
      
      <Section>
        <Partners />
      </Section>

      <Section>
        <CategoryPartnerHub />
      </Section>

      <Section>
        <BecomeAPartner />
      </Section>

      <Section>
        <WhyChooseInfoBoxx />
      </Section>

      {/* Testimonials */}
      <Section>
        <section className="bg-[#f7f4ec] px-4 py-20 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-7xl">
            <div className="flex flex-wrap gap-5 justify-between items-end mb-10">
              <div>
                <p className="text-xs font-black uppercase tracking-[0.25em] text-[#c9a000]">
                  Testimonials
                </p>
                <h2 className="mt-2 text-4xl font-black text-black">
                  What the journey feels like
                </h2>
              </div>
            </div>
            <div className="grid gap-4 md:grid-cols-3">
              {[
                ["I understood the land buying process before speaking to anyone. That changed everything.", "Adwoa", "Real estate user"],
                ["The legal page explained the steps clearly. I registered because I knew exactly what help I needed.", "Kojo", "Legal lead"],
                ["As a provider, this is better than cold leads. Users already know the problem before they contact us.", "Nana", "Sponsor partner"],
              ].map(([quote, name, role]) => (
                <motion.div
                  key={name}
                  whileHover={{ y: -6 }}
                  className="p-6 bg-white rounded-3xl border border-gray-100 shadow-sm"
                >
                  <div className="mb-5 flex gap-1 text-[#FFD221]">
                    {[1, 2, 3, 4, 5].map((i) => <Star key={i} size={16} fill="currentColor" />)}
                  </div>
                  <p className="text-sm leading-relaxed text-gray-600">&ldquo;{quote}&rdquo;</p>
                  <div className="mt-6">
                    <p className="font-black text-black">{name}</p>
                    <p className="text-xs font-semibold text-gray-400">{role}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      </Section>

      <FinalCTA />

      {/* Footer */}
      <motion.div
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true, margin: "-40px" }}
        transition={{ duration: 0.5 }}
      >
        <Footer />
      </motion.div>

      <ArticleAuthFlowModal
        open={subscribePromptOpen}
        onClose={dismissSubscribePrompt}
        onAuthenticated={dismissSubscribePrompt}
        openWith="signup"
        articleTitle="Infoboxx guides & expert content"
        sponsorName="verified providers"
        returnPath="/"
      />
    </div>
  );
};

export default Home;
